import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import TournamentIcon from '@mui/icons-material/EmojiEvents';
import TeamsIcon from '@mui/icons-material/Groups';
import GamesIcon from '@mui/icons-material/SportsMartialArts';
import RefereesIcon from '@mui/icons-material/Person';
import GenerateIcon from '@mui/icons-material/AutoAwesome';
import SyntheticIcon from '@mui/icons-material/Science';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import WarningIcon from '@mui/icons-material/Warning';
import GenerateRefereesIcon from '@mui/icons-material/PersonAdd';
import FieldsIcon from '@mui/icons-material/Map';
import RefreshIcon from '@mui/icons-material/Refresh';
import SimulateIcon from '@mui/icons-material/PlayArrow';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { adminApi } from '@/api/client';
import { clearJudgeGamesStorage } from '@/stores/gameStore';
import type { TournamentStats, GenerateTournamentRequest, SimulateRequest } from '@/types';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

function StatCard({ title, value, icon, color, subtitle }: StatCardProps) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: color + '20',
              color: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const theme = useTheme();
  const queryClient = useQueryClient();

  // State for generate dialogs
  const [syntheticDialogOpen, setSyntheticDialogOpen] = useState(false);
  const [kideDialogOpen, setKideDialogOpen] = useState(false);
  const [syntheticConfig, setSyntheticConfig] = useState({
    yle09_count: 512,
    yle12_count: 256,
    pro_count: 16,
  });
  const [kideFile, setKideFile] = useState<File | null>(null);
  const [kideFileError, setKideFileError] = useState<string>('');

  // State for clear dialogs
  const [clearTournamentDialogOpen, setClearTournamentDialogOpen] = useState(false);
  const [clearUsersDialogOpen, setClearUsersDialogOpen] = useState(false);
  const [clearAuditLogDialogOpen, setClearAuditLogDialogOpen] = useState(false);

  // State for referee generation dialog
  const [generateRefereesDialogOpen, setGenerateRefereesDialogOpen] = useState(false);
  const [generateRefereesCount, setGenerateRefereesCount] = useState(64);
  const [clearGeneratedRefereesDialogOpen, setClearGeneratedRefereesDialogOpen] = useState(false);

  // State for field management dialogs
  const [generateFieldsDialogOpen, setGenerateFieldsDialogOpen] = useState(false);
  const [generateFieldsCount, setGenerateFieldsCount] = useState(64);
  const [clearFieldsDialogOpen, setClearFieldsDialogOpen] = useState(false);

  // State for simulation dialogs
  const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
  const [simulateMode, setSimulateMode] = useState<'block' | 'round' | 'league'>('round');
  const [simulateLeague, setSimulateLeague] = useState('yle');
  const [simulateRound, setSimulateRound] = useState(1);
  const [simulateBlockId, setSimulateBlockId] = useState('');

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch tournament stats
  const { data: stats, isLoading, error } = useQuery<TournamentStats>({
    queryKey: ['tournamentStats'],
    queryFn: adminApi.getStats,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Generate tournament mutation
  const generateMutation = useMutation({
    mutationFn: (data: GenerateTournamentRequest) => adminApi.generateTournament(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournamentStats'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      // Clear judge localStorage to avoid stale game references
      clearJudgeGamesStorage();
      setSyntheticDialogOpen(false);
      setKideDialogOpen(false);
      setKideFile(null);
      setKideFileError('');
    },
  });

  // Clear tournament mutation
  const clearTournamentMutation = useMutation({
    mutationFn: () => adminApi.clearTournament(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tournamentStats'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['games'] });
      // Clear judge localStorage to avoid stale game references
      clearJudgeGamesStorage();
      setClearTournamentDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Cleared ${data.deleted.games} games, ${data.deleted.teams} teams, ${data.deleted.items} items`,
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: `Failed to clear tournament: ${error.message}`,
        severity: 'error',
      });
    },
  });

  // Clear users mutation
  const clearUsersMutation = useMutation({
    mutationFn: () => adminApi.clearUsers(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['referees'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setClearUsersDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Cleared ${data.deleted.referees} referees, ${data.deleted.users} users`,
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: `Failed to clear users: ${error.message}`,
        severity: 'error',
      });
    },
  });

  // Clear audit log mutation
  const clearAuditLogMutation = useMutation({
    mutationFn: () => adminApi.clearAuditLog(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
      setClearAuditLogDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Cleared ${data.deleted.audit_logs} event log entries`,
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: `Failed to clear event log: ${error.message}`,
        severity: 'error',
      });
    },
  });

  // Generate referees mutation
  const generateRefereesMutation = useMutation({
    mutationFn: (params: { count: number; seed?: number }) => adminApi.generateReferees(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['referees'] });
      setGenerateRefereesDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Generated ${data.referees_created} referees`,
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: `Failed to generate referees: ${error.message}`,
        severity: 'error',
      });
    },
  });

  // Clear generated referees mutation
  const clearGeneratedRefereesMutation = useMutation({
    mutationFn: () => adminApi.clearGeneratedReferees(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['referees'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setClearGeneratedRefereesDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Deleted ${data.deleted.referees} generated referees and ${data.deleted.users} users`,
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: `Failed to delete generated referees: ${error.message}`,
        severity: 'error',
      });
    },
  });

  // Generate fields mutation
  const generateFieldsMutation = useMutation({
    mutationFn: (params: { court_count: number }) => adminApi.seedFields(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fields'] });
      setGenerateFieldsDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Generated ${data.fields_created} fields`,
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: `Failed to generate fields: ${error.message}`,
        severity: 'error',
      });
    },
  });

  // Clear fields mutation
  const clearFieldsMutation = useMutation({
    mutationFn: () => adminApi.clearFields(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fields'] });
      setClearFieldsDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Cleared ${data.fields_deleted} fields`,
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: `Failed to clear fields: ${error.message}`,
        severity: 'error',
      });
    },
  });

  // Simulate mutation
  const simulateMutation = useMutation({
    mutationFn: (params: SimulateRequest) => adminApi.simulate(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tournamentStats'] });
      queryClient.invalidateQueries({ queryKey: ['games'] });
      setSimulateDialogOpen(false);
      const modeLabel = data.mode === 'block' ? `Block ${data.block_id}` :
        data.mode === 'round' ? `${data.league?.toUpperCase()} Round ${data.round}` :
          `${data.league?.toUpperCase()} league`;
      setSnackbar({
        open: true,
        message: `Simulated ${modeLabel}: ${data.games_played} games played`,
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: `Simulation failed: ${error.message}`,
        severity: 'error',
      });
    },
  });

  const handleGenerateSynthetic = () => {
    generateMutation.mutate({
      mode: 'synthetic',
      clear_existing: true,
      yle09_count: syntheticConfig.yle09_count,
      yle12_count: syntheticConfig.yle12_count,
      pro_count: syntheticConfig.pro_count,
    });
  };

  const handleGenerateKide = async () => {
    if (!kideFile) {
      setKideFileError('Please select a file');
      return;
    }

    try {
      const text = await kideFile.text();
      const parsedData = JSON.parse(text);
      generateMutation.mutate({
        mode: 'kide',
        clear_existing: true,
        kide_data: parsedData,
      });
    } catch {
      setKideFileError('Invalid JSON file');
    }
  };

  const handleKideFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setKideFileError('');
    if (file) {
      if (!file.name.endsWith('.json')) {
        setKideFileError('Please select a JSON file');
        setKideFile(null);
        return;
      }
      setKideFile(file);
    } else {
      setKideFile(null);
    }
  };

  const statCards = [
    {
      title: 'Total Teams',
      value: stats?.teams.total ?? 0,
      icon: <TeamsIcon fontSize="large" />,
      color: theme.palette.primary.main,
      subtitle: stats ? `YLE: ${stats.teams.yle} | PRO: ${stats.teams.pro} | FRM: ${stats.teams.frm}` : undefined,
    },
    {
      title: 'Total Games',
      value: stats?.games.total ?? 0,
      icon: <GamesIcon fontSize="large" />,
      color: theme.palette.secondary.main,
      subtitle: stats ? `YLE: ${stats.games.yle} | PRO: ${stats.games.pro} | FRM: ${stats.games.frm}` : undefined,
    },
    {
      title: 'Games In Progress',
      value: stats?.games_by_status.in_progress ?? 0,
      icon: <RefereesIcon fontSize="large" />,
      color: theme.palette.success.main,
      subtitle: stats ? `Finished: ${stats.games_by_status.finished}` : undefined,
    },
    {
      title: 'Scheduled Games',
      value: stats?.games_by_status.scheduled ?? 0,
      icon: <TournamentIcon fontSize="large" />,
      color: theme.palette.warning.main,
      subtitle: stats ? `Cancelled: ${stats.games_by_status.cancelled}` : undefined,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome to kyykka ATK - Tournament Management System
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load tournament stats
        </Alert>
      )}

      {/* Stats Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        {isLoading
          ? [1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={32} />
                </Box>
              </CardContent>
            </Card>
          ))
          : statCards.map((stat) => <StatCard key={stat.title} {...stat} />)}
      </Box>

      {/* Tournament Generation */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Tournament Generation
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate or regenerate games from existing teams. Clears all games and regenerates them
            using current VIP court preferences. Teams are preserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={generateMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              onClick={() => {
                generateMutation.mutate({ mode: 'games', clear_existing: true });
              }}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? 'Generating...' : 'Regenerate Games'}
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Development options: Generate synthetic teams + games for testing.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<SyntheticIcon />}
              onClick={() => setSyntheticDialogOpen(true)}
              disabled={generateMutation.isPending}
            >
              Generate Synthetic Data
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setKideDialogOpen(true)}
              disabled={generateMutation.isPending}
            >
              Import KideApp Data (Legacy)
            </Button>
          </Box>

          {generateMutation.isSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {generateMutation.data.message} - {generateMutation.data.games_created} games created
            </Alert>
          )}

          {generateMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Generation failed: {(generateMutation.error as Error).message}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Referee Management */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Referee Management
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate synthetic referees for development or testing.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<GenerateRefereesIcon />}
              onClick={() => setGenerateRefereesDialogOpen(true)}
              disabled={generateRefereesMutation.isPending || clearGeneratedRefereesMutation.isPending}
            >
              Generate Referees
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setClearGeneratedRefereesDialogOpen(true)}
              disabled={generateRefereesMutation.isPending || clearGeneratedRefereesMutation.isPending}
            >
              Delete Generated Referees
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Simulation */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Simulation
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Simulate game results for testing. Results are saved to the database.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<SimulateIcon />}
              onClick={() => setSimulateDialogOpen(true)}
              disabled={simulateMutation.isPending}
            >
              Simulate Games
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Field Management */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Field Management
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate or clear playing fields for the tournament venue.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<FieldsIcon />}
              onClick={() => setGenerateFieldsDialogOpen(true)}
              disabled={generateFieldsMutation.isPending}
            >
              Generate Fields
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setClearFieldsDialogOpen(true)}
              disabled={clearFieldsMutation.isPending}
            >
              Clear Fields
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Data Management */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Data Management
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Clear tournament data or user accounts. Use with caution - these actions cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setClearTournamentDialogOpen(true)}
              disabled={clearTournamentMutation.isPending || clearUsersMutation.isPending || clearAuditLogMutation.isPending}
            >
              Clear All (Games + Teams)
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setClearUsersDialogOpen(true)}
              disabled={clearTournamentMutation.isPending || clearUsersMutation.isPending || clearAuditLogMutation.isPending}
            >
              Clear Users & Referees
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setClearAuditLogDialogOpen(true)}
              disabled={clearTournamentMutation.isPending || clearUsersMutation.isPending || clearAuditLogMutation.isPending}
            >
              Clear Event Log
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Synthetic Data Dialog */}
      <Dialog
        open={syntheticDialogOpen}
        onClose={() => setSyntheticDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GenerateIcon color="primary" />
            Generate Synthetic Tournament
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create test tournament data with configurable team counts. This will clear existing data.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Yleinen 09:00 Teams"
              type="number"
              value={syntheticConfig.yle09_count}
              onChange={(e) =>
                setSyntheticConfig((prev) => ({
                  ...prev,
                  yle09_count: parseInt(e.target.value) || 0,
                }))
              }
              inputProps={{ min: 0, max: 1024 }}
              helperText="Max 1024 (full tournament: 512)"
            />
            <TextField
              label="Yleinen 12:00 Teams"
              type="number"
              value={syntheticConfig.yle12_count}
              onChange={(e) =>
                setSyntheticConfig((prev) => ({
                  ...prev,
                  yle12_count: parseInt(e.target.value) || 0,
                }))
              }
              inputProps={{ min: 0, max: 512 }}
              helperText="Max 512 (full tournament: 256)"
            />
            <TextField
              label="PRO Teams"
              type="number"
              value={syntheticConfig.pro_count}
              onChange={(e) =>
                setSyntheticConfig((prev) => ({
                  ...prev,
                  pro_count: parseInt(e.target.value) || 0,
                }))
              }
              inputProps={{ min: 0, max: 64 }}
              helperText="Max 64 (full tournament: 16)"
            />
            <Divider />
            <Typography variant="caption" color="text.secondary">
              Firma teams (24) are always generated from a fixed list.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyntheticDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGenerateSynthetic}
            disabled={generateMutation.isPending}
            startIcon={generateMutation.isPending ? <CircularProgress size={16} /> : <GenerateIcon />}
          >
            {generateMutation.isPending ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* KideApp Import Dialog */}
      <Dialog
        open={kideDialogOpen}
        onClose={() => {
          setKideDialogOpen(false);
          setKideFile(null);
          setKideFileError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon color="primary" />
            Import KideApp Data
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select the JSON export file from KideApp. This will clear existing data.
          </Typography>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: kideFileError ? 'error.main' : 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: 'action.hover',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.selected' },
            }}
            onClick={() => document.getElementById('kide-file-input')?.click()}
          >
            <input
              id="kide-file-input"
              type="file"
              accept=".json"
              onChange={handleKideFileChange}
              style={{ display: 'none' }}
            />
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" fontWeight={500}>
              {kideFile ? kideFile.name : 'Click to select JSON file'}
            </Typography>
            {kideFile && (
              <Typography variant="body2" color="text.secondary">
                {(kideFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            )}
          </Box>
          {kideFileError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {kideFileError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setKideDialogOpen(false);
              setKideFile(null);
              setKideFileError('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateKide}
            disabled={generateMutation.isPending || !kideFile}
            startIcon={generateMutation.isPending ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {generateMutation.isPending ? 'Importing...' : 'Import & Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Tournament Dialog */}
      <Dialog
        open={clearTournamentDialogOpen}
        onClose={() => setClearTournamentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Clear Tournament Data
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will permanently delete:
          </Typography>
          <Box component="ul" sx={{ mt: 0, pl: 3 }}>
            <li>All games, scores, and events</li>
            <li>All teams and items</li>
            <li>All game queue and message queue entries</li>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The following will be preserved:
          </Typography>
          <Box component="ul" sx={{ mt: 0, pl: 3 }}>
            <li>Tournament timetable configuration</li>
            <li>Users and referees</li>
            <li>Fields and field groups</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearTournamentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => clearTournamentMutation.mutate()}
            disabled={clearTournamentMutation.isPending}
            startIcon={clearTournamentMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {clearTournamentMutation.isPending ? 'Clearing...' : 'Clear Tournament Data'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Users Dialog */}
      <Dialog
        open={clearUsersDialogOpen}
        onClose={() => setClearUsersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Clear Users & Referees
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will permanently delete:
          </Typography>
          <Box component="ul" sx={{ mt: 0, pl: 3 }}>
            <li>All referee records</li>
            <li>All non-superuser accounts</li>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Superuser accounts will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearUsersDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => clearUsersMutation.mutate()}
            disabled={clearUsersMutation.isPending}
            startIcon={clearUsersMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {clearUsersMutation.isPending ? 'Clearing...' : 'Clear Users & Referees'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Audit Log Dialog */}
      <Dialog
        open={clearAuditLogDialogOpen}
        onClose={() => setClearAuditLogDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Clear Event Log
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will permanently delete all event log entries (audit records) shown on
            game, team, block, court, and referee detail pages.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use this to clear the audit trail before the tournament without affecting
            games, teams, or other data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAuditLogDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => clearAuditLogMutation.mutate()}
            disabled={clearAuditLogMutation.isPending}
            startIcon={clearAuditLogMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {clearAuditLogMutation.isPending ? 'Clearing...' : 'Clear Event Log'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Referees Dialog */}
      <Dialog
        open={generateRefereesDialogOpen}
        onClose={() => setGenerateRefereesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GenerateRefereesIcon color="primary" />
            Generate Synthetic Referees
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generate test referees with Finnish names for development and testing.
          </Typography>
          <TextField
            label="Number of Referees"
            type="number"
            value={generateRefereesCount}
            onChange={(e) => setGenerateRefereesCount(parseInt(e.target.value, 10) || 0)}
            fullWidth
            inputProps={{ min: 1, max: 500 }}
            helperText="Generates referee users with random Finnish names"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateRefereesDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => generateRefereesMutation.mutate({ count: generateRefereesCount })}
            disabled={generateRefereesMutation.isPending}
            startIcon={generateRefereesMutation.isPending ? <CircularProgress size={16} /> : <GenerateRefereesIcon />}
          >
            {generateRefereesMutation.isPending ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Generated Referees Dialog */}
      <Dialog
        open={clearGeneratedRefereesDialogOpen}
        onClose={() => setClearGeneratedRefereesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Delete Generated Referees
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will permanently delete referees and users with emails that start with "referee" and end with
            "@example.fi".
          </Typography>
          <Box component="ul" sx={{ mt: 0, pl: 3 }}>
            <li>Generated referee records</li>
            <li>Generated referee user accounts</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearGeneratedRefereesDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => clearGeneratedRefereesMutation.mutate()}
            disabled={clearGeneratedRefereesMutation.isPending}
            startIcon={
              clearGeneratedRefereesMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {clearGeneratedRefereesMutation.isPending ? 'Deleting...' : 'Delete Generated Referees'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Fields Dialog */}
      <Dialog
        open={generateFieldsDialogOpen}
        onClose={() => setGenerateFieldsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FieldsIcon color="primary" />
            Generate Playing Fields
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generate numbered playing fields for the tournament. For example, entering 127 will create fields named "1", "2", "3", ... "127".
          </Typography>
          <TextField
            label="Number of Fields"
            type="number"
            value={generateFieldsCount}
            onChange={(e) => setGenerateFieldsCount(parseInt(e.target.value, 10) || 0)}
            fullWidth
            inputProps={{ min: 1, max: 500 }}
            helperText="Creates fields numbered 1 through N"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateFieldsDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => generateFieldsMutation.mutate({ court_count: generateFieldsCount })}
            disabled={generateFieldsMutation.isPending}
            startIcon={generateFieldsMutation.isPending ? <CircularProgress size={16} /> : <FieldsIcon />}
          >
            {generateFieldsMutation.isPending ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Fields Dialog */}
      <Dialog
        open={clearFieldsDialogOpen}
        onClose={() => setClearFieldsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Clear All Fields
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will permanently delete:
          </Typography>
          <Box component="ul" sx={{ mt: 0, pl: 3 }}>
            <li>All field records</li>
            <li>All field group records</li>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Referees will have their field assignments cleared.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearFieldsDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => clearFieldsMutation.mutate()}
            disabled={clearFieldsMutation.isPending}
            startIcon={clearFieldsMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {clearFieldsMutation.isPending ? 'Clearing...' : 'Clear All Fields'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Simulate Dialog */}
      <Dialog
        open={simulateDialogOpen}
        onClose={() => setSimulateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SimulateIcon color="primary" />
            Simulate Games
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Simulate game results for testing. Random scores will be generated and saved to the database.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Mode</Typography>
            <ToggleButtonGroup
              value={simulateMode}
              exclusive
              onChange={(_, newMode) => newMode && setSimulateMode(newMode)}
              fullWidth
              size="small"
            >
              <ToggleButton value="block">Block</ToggleButton>
              <ToggleButton value="round">Round</ToggleButton>
              <ToggleButton value="league">League</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {simulateMode === 'block' && (
            <TextField
              label="Block ID"
              value={simulateBlockId}
              onChange={(e) => setSimulateBlockId(e.target.value)}
              fullWidth
              helperText="Enter the block ID (e.g., 1234 or 1234a)"
              placeholder="e.g., 1234 or 1234a"
              sx={{ mb: 2 }}
            />
          )}

          {(simulateMode === 'round' || simulateMode === 'league') && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>League</InputLabel>
              <Select
                value={simulateLeague}
                label="League"
                onChange={(e) => setSimulateLeague(e.target.value)}
              >
                <MenuItem value="yle">Yleinen</MenuItem>
                <MenuItem value="pro">PRO</MenuItem>
                <MenuItem value="frm">Firma</MenuItem>
              </Select>
            </FormControl>
          )}

          {simulateMode === 'round' && (
            <TextField
              label="Round Number"
              type="number"
              value={simulateRound}
              onChange={(e) => setSimulateRound(parseInt(e.target.value, 10) || 1)}
              fullWidth
              inputProps={{ min: 1, max: 10 }}
              helperText="Enter round number (1-10)"
            />
          )}

          <Alert severity="warning" sx={{ mt: 2 }}>
            This will save simulated results to the database. Use only for testing.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimulateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const params: SimulateRequest = { mode: simulateMode };
              if (simulateMode === 'block') {
                // Extract just the digits from the input (handles both "1234" and "1234a" formats)
                const digits = simulateBlockId.replace(/\D/g, '');
                params.block_id = digits ? parseInt(digits, 10) : 0;
              } else if (simulateMode === 'round') {
                params.league = simulateLeague;
                params.round = simulateRound;
              } else {
                params.league = simulateLeague;
              }
              simulateMutation.mutate(params);
            }}
            disabled={simulateMutation.isPending || (simulateMode === 'block' && !simulateBlockId.replace(/\D/g, ''))}
            startIcon={simulateMutation.isPending ? <CircularProgress size={16} /> : <SimulateIcon />}
          >
            {simulateMutation.isPending ? 'Simulating...' : 'Simulate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
